import { Button, EmptyState, Skeleton } from '@bennows/boows-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { History, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../lib/format';
import { fetchConversations } from './assistant-api';

interface Props {
  readonly onOpen: (id: string) => void;
  readonly onNew: () => void;
}

/** The member's own conversations, newest first, with "Neues Gespraech" (spec 110 section 4.15.6). */
export function HistoryView({ onOpen, onNew }: Props) {
  const { t } = useTranslation();
  const list = useInfiniteQuery({
    queryKey: ['assistant', 'conversations'],
    queryFn: ({ pageParam }) => fetchConversations(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    retry: false,
    gcTime: 0,
  });
  const conversations = list.data?.pages.flatMap((page) => page.data) ?? [];
  return (
    <div className="ui-assistant__history">
      <Button variant="secondary" size="sm" onClick={onNew}>
        {t('assistant.newConversation')}
      </Button>
      {list.isPending ? (
        <div role="status">
          <Skeleton variant="line" />
          <Skeleton variant="line" />
          <span className="ui-visually-hidden">{t('assistant.loading')}</span>
        </div>
      ) : list.isError && !list.data ? (
        <EmptyState
          variant="error"
          icon={<TriangleAlert aria-hidden="true" />}
          title={t('assistant.historyError')}
          action={
            <Button variant="secondary" size="sm" onClick={() => void list.refetch()}>
              {t('assistant.error.retry')}
            </Button>
          }
        />
      ) : conversations.length === 0 ? (
        <EmptyState variant="compact" icon={<History aria-hidden="true" />} title={t('assistant.historyEmpty')} />
      ) : (
        <ul className="ui-assistant__history-list" aria-label={t('assistant.historyTitle')}>
          {conversations.map((conversation) => (
            <li key={conversation.id}>
              <Button variant="ghost" block className="ui-assistant__history-item" aria-label={t('assistant.historyOpenConversation', { title: conversation.title })} onClick={() => onOpen(conversation.id)}>
                <span className="ui-assistant__history-title">{conversation.title}</span>
                <span className="ui-assistant__history-meta">{formatDateTime(conversation.last_activity_at)}</span>
              </Button>
            </li>
          ))}
        </ul>
      )}
      {list.isFetchNextPageError ? (
        <p className="ui-assistant__history-meta" role="alert">
          {t('assistant.historyError')}
        </p>
      ) : null}
      {list.hasNextPage ? (
        <Button variant="secondary" size="sm" loading={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
          {t('assistant.historyMore')}
        </Button>
      ) : null}
    </div>
  );
}
